import {
  Button,
  Checkbox,
  TextField,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import axios from 'axios'
import PageAppBar from 'components/PageAppBar'
import dayjs, { Dayjs } from 'dayjs'
import { base_url } from 'env'
import { useState, useEffect } from 'react'
import { useLoaderData, useNavigate } from 'react-router'
import { ProfileModel, UserModel } from 'types'
import { toast } from 'react-hot-toast'

const grades = [
  'PS',
  'MS',
  'GS',
  'CP',
  'CE1',
  'CE2',
  'CM1',
  'CM2',
  '6e',
  '5e',
  '4e',
  '3e',
  '2de',
  '1re',
  'Tle',
]

type ProfileEditState = Omit<ProfileModel, 'birthday'> & {
  birthday: Dayjs | null
}

export default function ProfileEditPage() {
  const navigate = useNavigate()
  const initialData = useLoaderData() as ProfileModel

  const [profile, setProfile] = useState<ProfileEditState>({
    ...initialData,
    birthday: initialData.birthday ? dayjs.unix(initialData.birthday) : null,
  })

  const [allUsers, setAllUsers] = useState<UserModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    axios
      .get(base_url + '/user/')
      .then((res) => {
        setAllUsers(res.data)
      })
      .catch((error) => {
        console.error('Failed to load users data', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    // Handle number conversion for timeTarget
    const isNumeric = ['timeTarget'].includes(name)
    setProfile((prev) => ({
      ...prev,
      [name]: isNumeric ? Number(value) : value,
    }))
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    setProfile((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSubmit = () => {
    if (!profile.firstName || !profile.birthday || !profile.grade) {
      toast.error('Prénom, date de naissance, et classe sont obligatoires.')
      return
    }

    setIsSaving(true)

    const payload: { [key: string]: any } = {
      firstName: profile.firstName,
      birthday: profile.birthday.unix(),
      grade: profile.grade,
      active: profile.active,
      testLocked: profile.testLocked,
      createdBy: profile.createdBy,
      timeTarget: profile.timeTarget, // Ajouté
    }
    if (profile.lastName) {
      payload.lastName = profile.lastName
    }

    axios
      .put(`${base_url}/profile/${profile.id}`, payload)
      .then((response) => {
        toast.success('Profil mis à jour avec succès !')
        navigate(`/profile/${response.data.id}`)
      })
      .catch((error) => {
        console.error('Failed to update profile:', error)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Chargement...">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName={`Modifier: ${profile.firstName}`}>
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Card sx={{ maxWidth: '800px', width: '100%' }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Détails du Profil
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="firstName"
                  label="Prénom"
                  value={profile.firstName}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="lastName"
                  label="Nom (Optionnel)"
                  value={profile.lastName || ''}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Date d'anniversaire"
                  value={profile.birthday}
                  onChange={(d) => setProfile((p) => ({ ...p, birthday: d }))}
                  sx={{ width: '100%' }}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isSaving}>
                  <InputLabel id="grade-select-label">Classe</InputLabel>
                  <Select
                    labelId="grade-select-label"
                    name="grade"
                    value={profile.grade}
                    label="Classe"
                    onChange={(e) =>
                      setProfile((p) => ({ ...p, grade: e.target.value }))
                    }
                  >
                    {grades.map((g) => (
                      <MenuItem key={g} value={g}>
                        {g}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={allUsers}
                  getOptionLabel={(option) => option.login}
                  value={
                    allUsers.find((u) => u.login === profile.createdBy) || null
                  }
                  onChange={(e, newValue) =>
                    setProfile((p) => ({
                      ...p,
                      createdBy: newValue?.login || '',
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Compte créateur" />
                  )}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="timeTarget"
                  label="Objectif de temps (min)"
                  type="number"
                  value={profile.timeTarget || ''}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="active"
                      checked={profile.active}
                      onChange={handleCheckboxChange}
                      disabled={isSaving}
                    />
                  }
                  label="Actif"
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="testLocked"
                      checked={profile.testLocked}
                      onChange={handleCheckboxChange}
                      disabled={isSaving}
                    />
                  }
                  label="Tests Verrouillés"
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Sauvegarder'}
            </Button>
          </CardActions>
        </Card>
      </Box>
    </PageAppBar>
  )
}
